import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, MessageCircle, Users, Star, Heart, Zap, ArrowRight, UserPlus, Target, Crown, Moon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GroupChatInterface from '../components/GroupChatInterface';
import { toast } from 'react-hot-toast';

import { useUserStore, Contact } from '../stores/userStore';
import { Link } from 'react-router-dom';
import StarAnimation from '../components/StarAnimation';
import { API_BASE_URL_WITH_VERSION } from '../config/api';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  brief_answer?: string;
  detailed_description?: string;
  timestamp: Date;
  readingType?: string;
  isLoading?: boolean;
}

interface GroupChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  brief_answer?: string;
  detailed_description?: string;
  timestamp: Date;
  taggedContacts?: Contact[];
  query?: string;
  isLoading?: boolean;
}

const Astrology: React.FC = () => {
  const { user } = useAuth();
  const { user: userStore, addContact } = useUserStore();
  const contacts = userStore?.contacts || [];
  const [selectedReading, setSelectedReading] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [groupMessages, setGroupMessages] = useState<GroupChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGroupChatOpen, setIsGroupChatOpen] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  // Load existing conversation when component mounts
  useEffect(() => {
    if (user?.uid) {
      loadExistingConversation();
    }
  }, [user?.uid]);

  const loadExistingConversation = async () => {
    try {
      setIsLoadingConversation(true);
      console.log('🔄 Loading existing conversation for user:', user?.uid);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('⚠️ No auth token available, starting fresh');
        createNewConversation();
        return;
      }
      
      // Try to get the most recent conversation
      const response = await fetch(`${API_BASE_URL_WITH_VERSION}/chat/recent-conversations?userId=${user?.uid}&limit=1`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.conversations && data.conversations.length > 0) {
          const conversation = data.conversations[0];
          setCurrentConversationId(conversation.id);
          console.log('📚 Found existing conversation:', conversation.id);
          
          // Load messages for this conversation
          await loadConversationMessages(conversation.id);
        } else {
          console.log('🆕 No existing conversations found, starting fresh');
          createNewConversation();
        }
      } else {
        console.log('⚠️ Failed to load conversations, starting fresh');
        createNewConversation();
      }
    } catch (error) {
      console.error('❌ Error loading conversation:', error);
      createNewConversation();
    } finally {
      setIsLoadingConversation(false);
    }
  };

  const loadConversationMessages = async (conversationId: string) => {
    try {
      console.log('📖 Loading messages for conversation:', conversationId);
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('⚠️ No auth token available for loading messages');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL_WITH_VERSION}/chat/history?conversationId=${conversationId}&limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.messages) {
          // Convert backend message format to frontend format
          const loadedMessages: Message[] = data.messages.map((msg: any) => ({
            id: msg.id || `msg_${Date.now()}_${Math.random()}`,
            type: msg.role === 'user' ? 'user' : 'assistant',
            content: msg.content,
            timestamp: new Date(msg.timestamp || msg.createdAt),
            readingType: msg.metadata?.readingType
          }));
          
          setMessages(loadedMessages);
          console.log('✅ Loaded', loadedMessages.length, 'messages');
        }
      }
    } catch (error) {
      console.error('❌ Error loading conversation messages:', error);
    }
  };

  const createNewConversation = () => {
    const newConversationId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setCurrentConversationId(newConversationId);
    console.log('🆕 Created new conversation:', newConversationId);
    setMessages([]);
  };

  const saveMessageToBackend = async (role: string, content: string, conversationId: string, metadata: any = {}) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('⚠️ No auth token available for saving message');
        return;
      }

      const response = await fetch(`${API_BASE_URL_WITH_VERSION}/chat/save-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          role,
          content,
          conversationId,
          metadata: {
            ...metadata,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (response.ok) {
        console.log('✅ Message saved to backend:', { role, conversationId });
      } else {
        console.warn('⚠️ Failed to save message to backend');
      }
    } catch (error) {
      console.error('❌ Error saving message to backend:', error);
    }
  };

  const handleChatMessage = async (message: string, title?: string, subtitle?: string, placeholder?: string) => {
    // Add user message to chat
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Save user message to backend
    if (currentConversationId) {
      await saveMessageToBackend('user', message, currentConversationId);
    }

    // Add loading message
    const loadingMessage: Message = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      content: 'Analyzing your request...',
      brief_answer: 'Analyzing...',
      detailed_description: 'Analyzing your request...',
      timestamp: new Date(),
      isLoading: true
    };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not available');
      }

      // Convert messages to conversation history format
      const conversationHistory = messages.map(msg => ({
        type: msg.type,
        content: msg.content,
        timestamp: msg.timestamp
      }));

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: message,
          userContext: {
            userId: user.uid
          },
          conversationHistory: conversationHistory
        })
      });

      const data = await response.json();

      // Remove loading message and add response
      setMessages(prev => prev.filter(msg => !msg.isLoading));
      
      const assistantMessage: Message = {
        id: (Date.now() + 3).toString(),
        type: 'assistant',
        content: data.response || 'I apologize, but I encountered an issue processing your request. Please try again.',
        brief_answer: data.brief_answer || data.response,
        detailed_description: data.detailed_description || data.response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Save assistant message to backend
      if (currentConversationId) {
        await saveMessageToBackend('assistant', data.response, currentConversationId, {
          readingType: 'astrology_chat'
        });
      }

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove loading message and add error
      setMessages(prev => prev.filter(msg => !msg.isLoading));
      
      const errorMessage: Message = {
        id: (Date.now() + 4).toString(),
        type: 'assistant',
        content: 'Sorry, I encountered an error while processing your request. Please check your connection and try again.',
        brief_answer: 'Error processing request',
        detailed_description: 'Sorry, I encountered an error while processing your request. Please check your connection and try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleGroupChatMessage = async (message: string, taggedContacts: Contact[]) => {
    // Add user message to group chat
    const userMessage: GroupChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      timestamp: new Date(),
      taggedContacts: taggedContacts
    };
    setGroupMessages(prev => [...prev, userMessage]);

    if (!user || !user.birthData) {
      // Add error message
      const errorMessage: GroupChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'Please provide your birth details in your profile first to get personalized relationship insights. You can add your birth date, time, and place in the Profile section.',
        timestamp: new Date()
      };
      setGroupMessages(prev => [...prev, errorMessage]);
      return;
    }

    // Add loading message
    const loadingMessage: GroupChatMessage = {
      id: (Date.now() + 2).toString(),
      type: 'assistant',
      content: 'Analyzing relationship dynamics and compatibility...',
      timestamp: new Date(),
      isLoading: true
    };
    setGroupMessages(prev => [...prev, loadingMessage]);

    try {
      // Create a comprehensive message that includes tagged contacts
      const contactNames = taggedContacts.map(c => c.name).join(', ');
      const enhancedMessage = `${message} ${taggedContacts.length > 0 ? `(Tagged: ${contactNames})` : ''}`;

      // Convert group messages to conversation history format
      const conversationHistory = groupMessages.map(msg => ({
        type: msg.type,
        content: msg.content,
        timestamp: msg.timestamp
      }));

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not available for group chat');
      }

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query: enhancedMessage,
          userContext: {
            userId: user.uid
          },
          conversationHistory: conversationHistory
        })
      });

      const data = await response.json();

      // Remove loading message and add response
      setGroupMessages(prev => prev.filter(msg => !msg.isLoading));
      
      const assistantMessage: GroupChatMessage = {
        id: (Date.now() + 3).toString(),
        type: 'assistant',
        content: data.response || 'I apologize, but I encountered an issue processing your relationship analysis. Please try again.',
        brief_answer: data.brief_answer || data.response,
        detailed_description: data.detailed_description || data.response,
        timestamp: new Date()
      };
      setGroupMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Error sending group message:', error);
      
      // Remove loading message and add error
      setGroupMessages(prev => prev.filter(msg => !msg.isLoading));
      
      const errorMessage: GroupChatMessage = {
        id: (Date.now() + 4).toString(),
        type: 'assistant',
        content: 'Sorry, I encountered an error while analyzing your relationships. Please check your connection and try again.',
        brief_answer: 'Error analyzing relationships',
        detailed_description: 'Sorry, I encountered an error while analyzing your relationships. Please check your connection and try again.',
        timestamp: new Date()
      };
      setGroupMessages(prev => [...prev, errorMessage]);
    }
  };

  const openGroupChat = () => {
    setIsGroupChatOpen(true);
  };

  const astrologyOptions = [
    {
      id: 'personal',
      title: 'Personal Astrology',
      description: 'Birth chart analysis & personality insights',
      icon: Star,
      color: 'from-purple-500 to-pink-500',
      action: () => setSelectedReading('personal')
    },
    {
      id: 'live',
      title: 'Live Chat with AI',
      description: 'Real-time conversations with AI astrologer',
      icon: MessageCircle,
      color: 'from-green-500 to-blue-500',
      action: () => setSelectedReading('live')
    },
    {
      id: 'relationships',
      title: 'Relationship Dynamics',
      description: 'Tag people & analyze compatibility',
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
      action: openGroupChat
    },
    {
      id: 'daily',
      title: 'Daily Guidance',
      description: 'Daily horoscopes & energy focus',
      icon: Moon,
      color: 'from-green-500 to-emerald-500',
      action: () => setSelectedReading('daily')
    },
    {
      id: 'advanced',
      title: 'Advanced Astrology',
      description: 'Deep cosmic insights & predictions',
      icon: Crown,
      color: 'from-orange-500 to-red-500',
      action: () => setSelectedReading('advanced')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Star Animation */}
      <StarAnimation starCount={25} />
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            <Sparkles className="inline-block w-10 h-10 text-purple-400 mr-3" />
            Astrology & Cosmic Insights
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Discover your cosmic blueprint, planetary influences, and spiritual guidance through personalized astrological analysis
          </p>
          
          {/* Conversation Status Display */}
          {currentConversationId && (
            <div className="mt-4 flex items-center justify-center space-x-2">
              <span className="text-sm text-gray-400">Active Conversation:</span>
              <span className="text-sm text-purple-300 font-mono bg-purple-900/30 px-2 py-1 rounded">
                {currentConversationId.substring(0, 8)}...
              </span>
              <button
                onClick={createNewConversation}
                className="text-sm text-blue-300 hover:text-blue-200 px-2 py-1 rounded hover:bg-blue-900/30 transition-colors"
              >
                New Chat
              </button>
            </div>
          )}
          
          {isLoadingConversation && (
            <div className="mt-2 flex items-center justify-center space-x-2 text-sm text-gray-400">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              <span>Loading conversation...</span>
            </div>
          )}
        </div>

        {/* Astrology Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          {astrologyOptions.map((option, index) => (
            <motion.button
              key={option.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.1 }}
              onClick={option.action}
              className="p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300 hover:scale-105 group"
            >
              <div className="flex items-center justify-center mb-4">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${option.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <option.icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-white font-semibold text-center mb-2">{option.title}</h3>
              <p className="text-gray-300 text-sm text-center">{option.description}</p>
            </motion.button>
          ))}
        </motion.div>

        {/* Personal Astrology Chat */}
        {selectedReading === 'personal' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-6 border border-purple-500/30">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-4">Personal Astrology Chat</h3>
                <p className="text-gray-300 mb-6">Ask about your birth chart, personality traits, and life path</p>
                <Link
                  to="/personal-chat"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 font-semibold"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Open Personal Chat
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Daily Guidance Chat */}
        {selectedReading === 'daily' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-4">Daily Guidance</h3>
                <p className="text-gray-300 mb-6">Get your daily horoscope and energy insights</p>
                <Link
                  to="/personal-chat"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 font-semibold"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Open Daily Guidance
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Advanced Astrology Chat */}
        {selectedReading === 'advanced' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-xl p-6 border border-indigo-500/30">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-4">Advanced Astrology</h3>
                <p className="text-gray-300 mb-6">Deep cosmic insights and predictions</p>
                <Link
                  to="/personal-chat"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 font-semibold"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Open Advanced Chat
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Live Chat with AI */}
        {selectedReading === 'live' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-xl p-6 border border-green-500/30">
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-4">Live Chat with AI</h3>
                <p className="text-gray-300 mb-6">Real-time text and voice conversations with our AI astrologer for instant guidance and personalized insights</p>
                <Link
                  to="/live-personal-chat"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-xl hover:from-green-600 hover:to-blue-600 transition-all duration-300 font-semibold"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Start Live Chat
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Relationship Dynamics Chat */}
        <GroupChatInterface
          isOpen={isGroupChatOpen}
          onClose={() => setIsGroupChatOpen(false)}
          onSendMessage={handleGroupChatMessage}
          messages={groupMessages}
          isLoading={isLoading}
          title="Relationship Dynamics"
          subtitle="Tag people and ask about compatibility, relationships, and group dynamics"
          placeholder="Ask about compatibility with @person or group dynamics..."
        />

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8"
        >
          <Link
            to="/profile"
            className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300 hover:scale-105 group"
          >
            <div className="flex items-center justify-center mb-3">
              <Target className="w-8 h-8 text-purple-400 group-hover:scale-110 transition-transform" />
            </div>
            <h3 className="text-white font-semibold text-center">Update Profile</h3>
            <p className="text-gray-300 text-sm text-center mt-2">Add birth details for personalized insights</p>
          </Link>

          <Link
            to="/matchmaking"
            className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300 hover:scale-105 group"
          >
            <div className="flex items-center justify-center mb-3">
              <Heart className="w-8 h-8 text-pink-400 group-hover:scale-110 transition-transform" />
            </div>
            <h3 className="text-white font-semibold text-center">Find Matches</h3>
            <p className="text-gray-300 text-sm text-center mt-2">Discover compatible partners</p>
          </Link>

          <Link
            to="/subscription"
            className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:border-purple-500/50 transition-all duration-300 hover:scale-105 group"
          >
            <div className="flex items-center justify-center mb-3">
              <Crown className="w-8 h-8 text-yellow-400 group-hover:scale-110 transition-transform" />
            </div>
            <h3 className="text-white font-semibold text-center">Premium Features</h3>
            <p className="text-gray-300 text-sm text-center mt-2">Unlock advanced insights</p>
          </Link>
        </motion.div>

        {/* Floating Action Button */}
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          onClick={openGroupChat}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 z-50"
        >
          <Users className="w-6 h-6 text-white" />
        </motion.button>
      </div>
    </div>
  );
};

export default Astrology; 